// The Transfers wire vocabulary and DTOs (SEP-283).
//
// The projection layer never imports the ontology (ADR-0003), so the closed
// sets below repeat `packages/ontology-dowgate/src/transfers/types.ts`. A server
// test pins the two copies to each other.
//
// Two shapes serve two readers. The OPS shape holds the whole ticket. The DESK
// shape holds what the Desk may see: no internal note, no Operations answer,
// no holding and no email.

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export const TRANSFER_STAGES = [
    'initiated',
    'in_progress_ops',
    'sent_to_titan',
    'in_progress_pershing',
    'completion_check',
    'complete',
] as const;
export const transferStageSchema = z.enum(TRANSFER_STAGES);
export type TransferStage = z.infer<typeof transferStageSchema>;

/** The five stages of a ticket that is still open. Complete is not one of them. */
export const ACTIVE_TRANSFER_STAGES = [
    'initiated',
    'in_progress_ops',
    'sent_to_titan',
    'in_progress_pershing',
    'completion_check',
] as const;
export const activeTransferStageSchema = z.enum(ACTIVE_TRANSFER_STAGES);
export type ActiveTransferStage = z.infer<typeof activeTransferStageSchema>;

/** Q2a: the request type (SEP-359). The type gives the layout of the rest of the checklist. */
export const TRANSFER_REQUEST_TYPES = [
    'transfer_in',
    'transfer_out',
    'internal_transfer',
    'demat',
    'remat',
    'renunciation',
    'stock_dump',
] as const;
export const transferRequestTypeSchema = z.enum(TRANSFER_REQUEST_TYPES);
export type TransferRequestType = z.infer<typeof transferRequestTypeSchema>;

/** The side of a transfer between two firms. Only a Transfer IN and a Transfer OUT have one. */
export const transferDirectionSchema = z.enum(['transfer_in', 'transfer_out']);
export type TransferDirection = z.infer<typeof transferDirectionSchema>;

/** True when the request type is a transfer between two firms: in or out. */
export function isTransferDirection(type: TransferRequestType): type is TransferDirection {
    return type === 'transfer_in' || type === 'transfer_out';
}

/**
 * True when the IM or the EA selects the holding lines at the submission: a
 * Transfer OUT, and an Internal Transfer from its "from" portfolio (SEP-359).
 * The ontology keeps the same rule (`transfers/stage.ts`).
 */
export function selectsTransferHoldings(type: TransferRequestType): boolean {
    return type === 'transfer_out' || type === 'internal_transfer';
}

/**
 * True when the submission carries the holding selection of the IM or the EA:
 * a Transfer OUT only. An Internal Transfer names its securities and their
 * quantities in its answers (SEP-359). The ontology keeps the same rule.
 */
export function submitsHoldingSelection(type: TransferRequestType): boolean {
    return type === 'transfer_out';
}

/**
 * True when Operations adds the holding lines of the ticket: a Transfer IN and
 * a Renunciation (SEP-359). Each other type gets its lines at the submission.
 */
export function operationsAddsTransferHoldings(type: TransferRequestType): boolean {
    return type === 'transfer_in' || type === 'renunciation';
}

/** Q2c of a Demat: the type of the Demat request (SEP-359). */
export const transferDematKindSchema = z.enum(['dematerialisation', 'drs_statement']);
export type TransferDematKind = z.infer<typeof transferDematKindSchema>;

export const transferPortfolioSchema = z.enum([
    'gia',
    'isa',
    'gia_and_isa',
    'jisa',
    'sipp',
    'trust',
    'bare_trust',
    'corporate',
]);
export type TransferPortfolio = z.infer<typeof transferPortfolioSchema>;

export const transferScopeSchema = z.enum(['full_in_specie', 'full_in_cash', 'partial']);
export type TransferScope = z.infer<typeof transferScopeSchema>;

/**
 * The reason of an Internal Transfer (SEP-359): the reasons of the Pershing
 * Transfer Request template, in the order of the template.
 */
export const transferInternalReasonSchema = z.enum([
    'beneficiary_inheritance',
    'gift',
    'divorce',
    'marriage',
    'change_of_trustee',
    'to_charity',
    'other',
    'spousal_gift',
    'additional_permitted_subscriptions',
    'death',
]);
export type TransferInternalReason = z.infer<typeof transferInternalReasonSchema>;

/** The Book Value Treatment of an Internal Transfer (SEP-359), as the Pershing template gives it. */
export const transferBookValueTreatmentSchema = z.enum(['zero_book', 'book_cost_listed']);
export type TransferBookValueTreatment = z.infer<typeof transferBookValueTreatmentSchema>;

/**
 * The Transfer Type that Operations chooses for a ticket (SEP-291). The order is
 * the order of the list of the firm. `TRANSFER_TYPE_LABEL` holds the code and
 * the name of each value.
 */
export const TRANSFER_TYPES = [
    'out_full_cash',
    'out_full_kind',
    'out_partial_cash',
    'out_partial_kind',
    'gift',
    'in_full_cash',
    'in_full_kind',
    'in_partial_cash',
    'in_partial_kind',
    'stock_dump',
    'dematerialise',
    'rematerialise',
    'renunciation',
    'charitable_gift',
    'internal_transfer',
    'book_cost_adjustment',
] as const;
export const transferTypeSchema = z.enum(TRANSFER_TYPES);
export type TransferType = z.infer<typeof transferTypeSchema>;

export const transferYesNoSchema = z.enum(['yes', 'no']);
export type TransferYesNo = z.infer<typeof transferYesNoSchema>;

export const transferOutScopeSchema = z.enum(['full', 'partial']);
export type TransferOutScope = z.infer<typeof transferOutScopeSchema>;

export const transferTradingIntentSchema = z.enum(['no_further_trading', 'further_trading']);
export type TransferTradingIntent = z.infer<typeof transferTradingIntentSchema>;

export const transferOutReasonSchema = z.enum([
    'deceased',
    'poor_performance',
    'liquidation',
    'charges_costs',
    'consolidation',
    'client_unhappy_complaint',
    'other',
]);
export type TransferOutReason = z.infer<typeof transferOutReasonSchema>;

export const transferOwnerTeamSchema = z.enum(['operations', 'desk']);
export type TransferOwnerTeam = z.infer<typeof transferOwnerTeamSchema>;

export const transferNoteVisibilitySchema = z.enum(['internal', 'desk']);
export type TransferNoteVisibility = z.infer<typeof transferNoteVisibilitySchema>;

/** Who reads a history row (SEP-317). Null on a row that names no visibility. */
export const transferEventVisibilitySchema = z.enum(['internal', 'desk']);
export type TransferEventVisibility = z.infer<typeof transferEventVisibilitySchema>;

export const transferEventTypeSchema = z.enum([
    'submitted',
    'stage_changed',
    'owner_changed',
    'transfer_type_changed',
    'happy_fox_ref_changed',
    'operations_answer_changed',
    'note_added',
    'document_uploaded',
    'email_sent',
    'email_not_sent',
    'holding_included',
    'holding_excluded',
    'holding_confirmed',
    'completed',
    'recalled',
    'holding_status_changed',
    'holding_note_added',
    'holdings_bulk_confirmed',
    'holding_added',
    'completion_challenged',
    'challenge_resolved',
    'holding_desk_responded',
    'holding_response_acknowledged',
    'note_edited',
    'note_removed',
    'answer_edited',
    'securities_sale_confirmed',
]);
export type TransferEventType = z.infer<typeof transferEventTypeSchema>;

export const transferEmailDeliverySchema = z.enum(['sent', 'not_sent', 'failed']);
export type TransferEmailDelivery = z.infer<typeof transferEmailDeliverySchema>;

/**
 * The selection state of a holding line, from its inclusion and its confirmed
 * values. The server derives it, and no Action stores it.
 */
export const transferHoldingSelectionStatusSchema = z.enum([
    'not_applicable',
    'included',
    'confirmed',
    'excluded',
]);
export type TransferHoldingSelectionStatus = z.infer<typeof transferHoldingSelectionStatusSchema>;

/**
 * The stored status of one holding line (SEP-317 section 4.2, SEP-359). The
 * request type gives the statuses that a line can have
 * (`holdingStatusesOf`). The last five values are retired: old history rows
 * name them and a Stock Dump line keeps `not_started`, but no dropdown offers
 * them.
 */
export const TRANSFER_HOLDING_STATUSES = [
    'awaiting_settlement_date',
    'need_to_sell',
    'unable_to_transfer',
    'im_response',
    'pending_certificate',
    'under_investigation',
    'no_longer_transferring',
    'settled',
    'awaiting_demat',
    'awaiting_remat',
    'queried_with_im',
    'awaiting_settlement',
    'in_process',
    'compliance_review',
    'not_started',
    'in_progress',
    'awaiting_information',
    'action_required_desk',
    'complete',
] as const;
export const transferHoldingStatusSchema = z.enum(TRANSFER_HOLDING_STATUSES);
export type TransferHoldingStatus = z.infer<typeof transferHoldingStatusSchema>;

/** Who acts next on a holding line (SEP-359). */
export const TRANSFER_HOLDING_WORKFLOW_STATES = [
    'none',
    'im_action_required',
    'awaiting_operations_review',
] as const;
export const transferHoldingWorkflowStateSchema = z.enum(TRANSFER_HOLDING_WORKFLOW_STATES);
export type TransferHoldingWorkflowState = z.infer<typeof transferHoldingWorkflowStateSchema>;

/** The confirmations of Operations before it completes a ticket (SEP-359). */
export const TRANSFER_COMPLETION_CHECKS = [
    'stock_transferred',
    'book_costs_correct',
    'out_fees_charged',
    'custody_fees_charged',
    'assets_reconciled',
    'stock_deposited',
    'stock_removed',
] as const;
export const transferCompletionCheckSchema = z.enum(TRANSFER_COMPLETION_CHECKS);
export type TransferCompletionCheck = z.infer<typeof transferCompletionCheckSchema>;

// The rules below repeat `transfers/holding-status.ts` of the ontology, and a
// server test pins the two copies to each other. Each per-type rule is a
// function with a switch, so it holds no object key.

/** The statuses that a line of the request type can have, in the order of the dropdown. A Stock Dump has none. */
export function holdingStatusesOf(type: TransferRequestType): ReadonlyArray<TransferHoldingStatus> {
    switch (type) {
        case 'transfer_in':
        case 'transfer_out':
            return [
                'awaiting_settlement_date',
                'need_to_sell',
                'unable_to_transfer',
                'im_response',
                'pending_certificate',
                'under_investigation',
                'no_longer_transferring',
                'settled',
            ];
        case 'demat':
            return ['awaiting_demat', 'settled', 'queried_with_im'];
        case 'remat':
            return ['awaiting_remat', 'settled', 'queried_with_im'];
        case 'internal_transfer':
            return ['awaiting_settlement', 'queried_with_im', 'settled'];
        case 'renunciation':
            return ['in_process', 'compliance_review', 'queried_with_im', 'settled'];
        case 'stock_dump':
            return [];
    }
}

/** The statuses that refer a line of the request type to the IM. */
export function referralStatusesOf(
    type: TransferRequestType,
): ReadonlyArray<TransferHoldingStatus> {
    switch (type) {
        case 'transfer_in':
        case 'transfer_out':
            return ['need_to_sell', 'unable_to_transfer'];
        case 'demat':
        case 'remat':
        case 'internal_transfer':
        case 'renunciation':
            return ['queried_with_im'];
        case 'stock_dump':
            return [];
    }
}

/**
 * What a change to a holding status needs:
 *
 * - `desk_note`: a comment that says what the IM must do. It is always
 *   visible to the Desk (a referral status).
 * - `reason`: a reason. Operations chooses who can see it.
 * - `none`: nothing. A note is optional.
 */
export type TransferHoldingStatusNoteRule = 'desk_note' | 'reason' | 'none';

/** The note rule of a change of a line of the request type to the status. */
export function holdingStatusNoteRule(
    type: TransferRequestType,
    status: TransferHoldingStatus,
): TransferHoldingStatusNoteRule {
    if (referralStatusesOf(type).includes(status)) return 'desk_note';
    if (status === 'no_longer_transferring') return 'reason';
    return 'none';
}

/** True when the status closes the line: Settled or No Longer Transferring. */
export function isSettledHoldingStatus(status: TransferHoldingStatus): boolean {
    return status === 'settled' || status === 'no_longer_transferring';
}

/** One completion check, with the words of the card. */
export interface TransferCompletionCheckItem {
    readonly value: TransferCompletionCheck;
    readonly text: string;
}

/** The completion checks of the request type, in the order of the card. A Remat has none. */
export function transferCompletionChecksOf(
    type: TransferRequestType,
): ReadonlyArray<TransferCompletionCheckItem> {
    switch (type) {
        case 'transfer_in':
        case 'internal_transfer':
            return [
                {
                    value: 'stock_transferred',
                    text: 'Please confirm all stock has been transferred correctly.',
                },
                {
                    value: 'book_costs_correct',
                    text: 'Please confirm if all the book costs are correct.',
                },
            ];
        case 'transfer_out':
            return [
                {
                    value: 'out_fees_charged',
                    text: 'Please confirm Transfer Out fees have been charged.',
                },
                {
                    value: 'custody_fees_charged',
                    text: 'Please confirm Management/Custody Fees have been charged.',
                },
                {
                    value: 'assets_reconciled',
                    text: 'Please confirm that all expected assets and cash have been received and reconciled.',
                },
            ];
        case 'demat':
        case 'stock_dump':
            return [
                {
                    value: 'stock_deposited',
                    text: 'Please confirm all stock has been deposited correctly',
                },
            ];
        case 'renunciation':
            return [
                {
                    value: 'stock_removed',
                    text: 'Please confirm all stock has been removed correctly',
                },
            ];
        case 'remat':
            return [];
    }
}

/** The words of one completion check value, or the value when it is not known. */
export function transferCompletionCheckText(value: string): string {
    for (const type of TRANSFER_REQUEST_TYPES) {
        const item = transferCompletionChecksOf(type).find((check) => check.value === value);
        if (item) return item.text;
    }
    return value;
}

/** Why a line stops the completion of its ticket (SEP-359). */
export type TransferCompletionBlock = 'awaiting_im' | 'awaiting_review' | 'not_settled';

/**
 * The block of one line, or null when the line does not stop the completion.
 * An excluded line is free. A Stock Dump is free of the settlement rule.
 */
export function transferCompletionBlockOf(
    type: TransferRequestType,
    line: {
        readonly included: boolean | null;
        readonly holdingStatus: TransferHoldingStatus;
        readonly workflowState: TransferHoldingWorkflowState;
    },
): TransferCompletionBlock | null {
    if (line.included === false) return null;
    if (line.workflowState === 'im_action_required') return 'awaiting_im';
    if (line.workflowState === 'awaiting_operations_review') return 'awaiting_review';
    if (type !== 'stock_dump' && !isSettledHoldingStatus(line.holdingStatus)) return 'not_settled';
    return null;
}

/** The workflow label of a holding line (SEP-359), as the card names it. */
export type TransferHoldingWorkflowLabel =
    | 'im_action_required'
    | 'awaiting_operations_review'
    | 'complete'
    | 'operations';

/** The team for the next action on a holding line. Null for a complete line. */
export type TransferHoldingNextAction = 'im' | 'operations' | null;

/** The workflow label and the next action of a holding line. */
export function transferHoldingWorkflowOf(line: {
    readonly holdingStatus: TransferHoldingStatus;
    readonly workflowState: TransferHoldingWorkflowState;
}): { label: TransferHoldingWorkflowLabel; nextAction: TransferHoldingNextAction } {
    if (line.workflowState === 'im_action_required') {
        return { label: 'im_action_required', nextAction: 'im' };
    }
    if (line.workflowState === 'awaiting_operations_review') {
        return { label: 'awaiting_operations_review', nextAction: 'operations' };
    }
    if (isSettledHoldingStatus(line.holdingStatus)) return { label: 'complete', nextAction: null };
    return { label: 'operations', nextAction: 'operations' };
}

/** The purpose of a document of a ticket (SEP-317). An older document reads as `general`. */
export const transferDocumentPurposeSchema = z.enum([
    'general',
    'portfolio_valuation',
    'holdings_file',
    'pershing_request',
]);
export type TransferDocumentPurpose = z.infer<typeof transferDocumentPurposeSchema>;

/** The tone of the Last Updated cell. See `lastUpdatedTone` in `transfer-format.ts`. */
export const transferTimeToneSchema = z.enum(['green', 'amber', 'red']);
export type TransferTimeTone = z.infer<typeof transferTimeToneSchema>;

// ---------------------------------------------------------------------------
// The submitted answers (Q1 to Q6 and the comments)
// ---------------------------------------------------------------------------

// The ontology holds the same rules (`packages/ontology-dowgate/src/transfers/answers.ts`).
// Keep the two copies the same: a server test parses the same answers with both.

const requiredText = z.string().trim().min(1).max(500);
const optionalText = z.string().trim().max(2000).nullable();
const optionalShortText = z.string().trim().max(200).nullable();
const yesNo = transferYesNoSchema;

const ANSWER_THIS = 'Answer this question.';

function issue(ctx: z.RefinementCtx, path: (string | number)[], message: string): void {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
}

// ---------------------------------------------------------------------------
// The parts that the blocks of SEP-359 share
// ---------------------------------------------------------------------------

/**
 * One portfolio that the checklist names (SEP-359). The snapshot keeps the
 * reference and the client name as the form showed them, so the ticket reads
 * the same after the client record changes.
 */
export const transferPortfolioChoiceSchema = z.object({
    /** The Portfolio row. */
    portfolioId: z.string().min(1),
    /** The portfolio reference, as the client record holds it. */
    reference: z.string().trim().min(1).max(120),
    /** The client of the portfolio. */
    clientId: z.string().min(1),
    clientName: requiredText,
});
export type TransferPortfolioChoice = z.infer<typeof transferPortfolioChoiceSchema>;

/** One stock of a Demat, a Remat or a Stock Dump (SEP-359). */
export const transferStockLineSchema = z.object({
    securityName: z.string().trim().min(1).max(200),
    isin: z.string().trim().max(12).nullable(),
    sedol: z.string().trim().max(7).nullable(),
    /**
     * The ClientHolding row of a Remat line: the holding of the portfolio.
     * Null for a stock that the portfolio does not hold yet.
     */
    clientHoldingId: z.string().min(1).nullable(),
    quantity: z.number().finite().positive(),
});
export type TransferStockLine = z.infer<typeof transferStockLineSchema>;

/** The fee instructions of a Demat (Q4) and of a Remat (Q5). */
export const transferFeeInstructionSchema = z
    .object({
        /** Q4a or Q5a: should the applicable fee be charged? */
        chargeFee: yesNo,
        /** Q4b or Q5b on a yes: the portfolio that pays the fee. */
        feePortfolio: transferPortfolioChoiceSchema.nullable(),
        /** Q4b or Q5b on a no: the reason to waive the fee. */
        waiverReason: optionalText,
    })
    .superRefine((value, ctx) => {
        if (value.chargeFee === 'yes' && value.feePortfolio === null) {
            issue(ctx, ['feePortfolio'], ANSWER_THIS);
        }
        if (value.chargeFee === 'no' && !value.waiverReason?.trim()) {
            issue(ctx, ['waiverReason'], ANSWER_THIS);
        }
    });
export type TransferFeeInstruction = z.infer<typeof transferFeeInstructionSchema>;

// ---------------------------------------------------------------------------
// Transfer OUT: Q5 and Q6
// ---------------------------------------------------------------------------

/** The fields of Q5a to Q6c without Q5d and Q6a. */
const transferOutShape = {
    /** Q5a. The submission derives it from Q2c (SEP-317), so the form does not ask it. */
    outScope: transferOutScopeSchema,
    /** Q5b */
    salesSettled: yesNo,
    /** Q5c */
    tradingIntent: transferTradingIntentSchema,
    /** Q5e */
    chargesDebited: yesNo,
    /** Q5e follow-up: the director who approved the waiver. Required when Q5e is no. */
    feeWaiverDirector: optionalShortText,
    /** Q6b */
    mandatesToCancel: yesNo,
    /**
     * Q6c. Only a full transfer asks it (SEP-359), so a partial transfer that
     * is submitted after SEP-359 keeps null. An older ticket keeps its answer.
     */
    reason: transferOutReasonSchema.nullable(),
    /** Q6c "Other (explain below)": required when the reason is `other`. */
    reasonOther: optionalText,
};

/** The rules of the transfer out block. The stored block and the submitted block share them. */
function refineTransferOut(
    value: {
        readonly chargesDebited: string;
        readonly feeWaiverDirector: string | null;
        readonly reason: string | null;
        readonly reasonOther: string | null;
    },
    ctx: z.RefinementCtx,
): void {
    if (value.chargesDebited === 'no' && !value.feeWaiverDirector?.trim()) {
        issue(ctx, ['feeWaiverDirector'], 'Name the director who approved the waiver.');
    }
    if (value.reason === 'other' && !value.reasonOther?.trim()) {
        issue(ctx, ['reasonOther'], 'Explain the reason.');
    }
}

/** Q5a to Q6c without Q5d and Q6a. Only a transfer out has this block. */
export const transferOutAnswersSchema = z.object(transferOutShape).superRefine(refineTransferOut);
export type TransferOutAnswers = z.infer<typeof transferOutAnswersSchema>;

/**
 * The transfer out block of a submission (SEP-317 section 2.8). Q5a is
 * optional, because the submit Action derives it from Q2c.
 */
export const transferOutSubmissionSchema = z
    .object({ ...transferOutShape, outScope: transferOutScopeSchema.optional() })
    .superRefine(refineTransferOut);
export type TransferOutSubmission = z.infer<typeof transferOutSubmissionSchema>;

// ---------------------------------------------------------------------------
// The blocks of the other request types (SEP-359)
// ---------------------------------------------------------------------------

/**
 * One security of an Internal Transfer (SEP-359): a holding of the "from"
 * portfolio, the quantity that moves, and the book cost when the Book Value
 * Treatment is `book_cost_listed`. The SEDOL and the name come from the
 * holding, so the Pershing Transfer Request can list them.
 */
export const transferInternalSecuritySchema = z.object({
    /** The ClientHolding row of the "from" portfolio. */
    clientHoldingId: z.string().min(1),
    securityName: z.string().trim().min(1).max(200),
    isin: z.string().trim().max(12).nullable(),
    sedol: z.string().trim().max(7).nullable(),
    /** The quantity that moves. It is not more than the holding. */
    quantity: z.number().finite().positive(),
    /** The book cost in GBP that the IM takes from Tercero. Null for `zero_book`. */
    bookCost: z.number().finite().nonnegative().nullable(),
});
export type TransferInternalSecurity = z.infer<typeof transferInternalSecuritySchema>;

/**
 * The fields of the Internal Transfer Specifications (Q3). Q2b stays a flat
 * answer. A ticket from before the Pershing questions (SEP-359) has no
 * reason, no Book Value Treatment and no securities, so the stored block
 * allows null and an empty list.
 */
const internalShape = {
    /** Q3a: are the holdings transferred between portfolios of the same client? */
    sameClient: yesNo,
    /** The portfolio of the client that the holdings leave. */
    fromPortfolio: transferPortfolioChoiceSchema,
    /** The portfolio that receives the holdings: of the same client on a yes, of any client of the book on a no. */
    toPortfolio: transferPortfolioChoiceSchema,
    /** The Reason for Transfer of the Pershing template. */
    reason: transferInternalReasonSchema.nullable().default(null),
    /** The reason in words: required when the reason is `other`. */
    reasonOther: optionalText.default(null),
    /** The date of death: required when the reason is `death`. */
    dateOfDeath: z.string().date().nullable().default(null),
    /** The Book Value Treatment of the Pershing template. */
    bookValueTreatment: transferBookValueTreatmentSchema.nullable().default(null),
    /** The securities that move, with the quantity of each. */
    securities: z.array(transferInternalSecuritySchema).max(500).default([]),
};

/** The rules of the Internal Transfer block. The stored block and the submitted block share them. */
function refineInternal(
    value: {
        readonly sameClient: string;
        readonly fromPortfolio: TransferPortfolioChoice;
        readonly toPortfolio: TransferPortfolioChoice;
        readonly reason: string | null;
        readonly reasonOther: string | null;
        readonly dateOfDeath: string | null;
        readonly bookValueTreatment: string | null;
        readonly securities: ReadonlyArray<TransferInternalSecurity>;
    },
    ctx: z.RefinementCtx,
): void {
    if (value.fromPortfolio.portfolioId === value.toPortfolio.portfolioId) {
        issue(ctx, ['toPortfolio'], 'Choose a different portfolio.');
    }
    if (value.sameClient === 'yes' && value.fromPortfolio.clientId !== value.toPortfolio.clientId) {
        issue(ctx, ['toPortfolio'], 'Choose a portfolio of the same client.');
    }
    if (value.reason === 'other' && !value.reasonOther?.trim()) {
        issue(ctx, ['reasonOther'], 'Explain the reason.');
    }
    if (value.reason === 'death' && value.dateOfDeath === null) {
        issue(ctx, ['dateOfDeath'], ANSWER_THIS);
    }
    const seen = new Set<string>();
    value.securities.forEach((security, index) => {
        if (seen.has(security.clientHoldingId)) {
            issue(ctx, ['securities', index, 'clientHoldingId'], 'Choose each holding once.');
        }
        seen.add(security.clientHoldingId);
        if (value.bookValueTreatment === 'book_cost_listed' && security.bookCost === null) {
            issue(ctx, ['securities', index, 'bookCost'], 'Enter the book cost.');
        }
    });
}

/** The Internal Transfer Specifications (Q3), as the ticket stores them. */
export const transferInternalAnswersSchema = z.object(internalShape).superRefine(refineInternal);
export type TransferInternalAnswers = z.infer<typeof transferInternalAnswersSchema>;

/**
 * The Internal Transfer block of a submission (SEP-359). The Pershing
 * questions are mandatory: the reason, the Book Value Treatment and at least
 * one security.
 */
export const transferInternalSubmissionSchema = z
    .object({
        ...internalShape,
        reason: transferInternalReasonSchema,
        bookValueTreatment: transferBookValueTreatmentSchema,
        securities: z.array(transferInternalSecuritySchema).min(1).max(500),
    })
    .superRefine(refineInternal);
export type TransferInternalSubmission = z.infer<typeof transferInternalSubmissionSchema>;

/** The Demat questions: Q2b, Q2c, Q3 (stock details), Q4 (fee) and Q5 (original documents). */
export const transferDematAnswersSchema = z.object({
    /** Q2b: the portfolio that receives the stock. */
    portfolio: transferPortfolioChoiceSchema,
    /** Q2c: the type of the Demat request. */
    dematKind: transferDematKindSchema,
    /** Q3a and Q3b, with "Add another stock". */
    stocks: z.array(transferStockLineSchema).min(1).max(50),
    /** Q4a and Q4b. */
    fee: transferFeeInstructionSchema,
    /** Q5a: the original documents are with the Transfers Team. */
    originalsHandedOver: yesNo,
});
export type TransferDematAnswers = z.infer<typeof transferDematAnswersSchema>;

/** The postal address of a share certificate (Remat Q4b). */
export const transferCertificateAddressSchema = z.object({
    line1: z.string().trim().min(1).max(200),
    line2: z.string().trim().max(200).nullable(),
    town: z.string().trim().min(1).max(100),
    county: z.string().trim().max(100).nullable(),
    postcode: z.string().trim().min(1).max(20),
    country: z.string().trim().min(1).max(100),
});
export type TransferCertificateAddress = z.infer<typeof transferCertificateAddressSchema>;

/** The Remat questions: Q2b, Q3 (stock details), Q4 (certificate registration) and Q5 (fee). */
export const transferRematAnswersSchema = z
    .object({
        /** Q2b: the portfolio that the stock leaves. */
        portfolio: transferPortfolioChoiceSchema,
        /** Q3a and Q3b, with "Add another stock". Each line is a holding of the portfolio. */
        stocks: z.array(transferStockLineSchema).min(1).max(50),
        /** Q4a: the full name on the share certificate. */
        certificateName: z.string().trim().min(1).max(200),
        /** Q4b: the address of the share certificate. */
        certificateAddress: transferCertificateAddressSchema,
        /** Q5a and Q5b. */
        fee: transferFeeInstructionSchema,
    })
    .superRefine((value, ctx) => {
        value.stocks.forEach((stock, index) => {
            if (stock.clientHoldingId === null) {
                issue(
                    ctx,
                    ['stocks', index, 'clientHoldingId'],
                    'Choose a holding of the portfolio.',
                );
            }
        });
    });
export type TransferRematAnswers = z.infer<typeof transferRematAnswersSchema>;

/** The Renunciation questions: Q2b and Q4a (original documents). */
export const transferRenunciationAnswersSchema = z.object({
    /** Q2b: the portfolio that the holdings are renounced from. */
    portfolio: transferPortfolioChoiceSchema,
    /** Q4a: the original documents are with the Transfers Team. */
    originalsHandedOver: yesNo,
});
export type TransferRenunciationAnswers = z.infer<typeof transferRenunciationAnswersSchema>;

/** The Stock Dump questions: Q2b, Q2c and Q3 (stock details). */
export const transferStockDumpAnswersSchema = z.object({
    /** Q2b: the account or portfolio that receives the stock. */
    portfolio: transferPortfolioChoiceSchema,
    /** Q2c: the CREST ID of the counterparty. */
    crestId: z.string().trim().min(1).max(60),
    /** Q3a and Q3b: one stock and its quantity. */
    stock: transferStockLineSchema,
    /** Q3c: the expected date of the stock dump. */
    expectedDate: z.string().date(),
});
export type TransferStockDumpAnswers = z.infer<typeof transferStockDumpAnswersSchema>;

// ---------------------------------------------------------------------------
// Which questions each request type asks
// ---------------------------------------------------------------------------

/** The key of the answer block of a request type (SEP-359). */
type TransferBlockKey =
    | 'transferOut'
    | 'internalTransfer'
    | 'demat'
    | 'remat'
    | 'renunciation'
    | 'stockDump';

/** The block key of each request type. A Transfer IN has no block. */
export function transferRequestTypeBlock(type: TransferRequestType): TransferBlockKey | null {
    switch (type) {
        case 'transfer_in':
            return null;
        case 'transfer_out':
            return 'transferOut';
        case 'internal_transfer':
            return 'internalTransfer';
        case 'demat':
            return 'demat';
        case 'remat':
            return 'remat';
        case 'renunciation':
            return 'renunciation';
        case 'stock_dump':
            return 'stockDump';
    }
}

const BLOCK_KEYS: readonly TransferBlockKey[] = [
    'transferOut',
    'internalTransfer',
    'demat',
    'remat',
    'renunciation',
    'stockDump',
];

/** True when the type asks Q2b: the portfolios. */
export function asksTransferPortfolios(type: TransferRequestType): boolean {
    return isTransferDirection(type) || type === 'internal_transfer';
}

/**
 * True when the type asks Q2c: a full or a partial transfer. An Internal
 * Transfer does not ask it (SEP-359): the IM selects each security and its
 * quantity. An Internal Transfer from before that change keeps its answer.
 */
export function asksTransferScope(type: TransferRequestType): boolean {
    return isTransferDirection(type);
}

/** True when the type asks Q3: the counterparty. Only a transfer between two firms has one. */
export function asksCounterparty(type: TransferRequestType): boolean {
    return isTransferDirection(type);
}

/**
 * True when the type asks Q2d: "Have all outstanding management fees been
 * deducted prior to the transfer?" (SEP-359). Only a Transfer OUT asks it.
 * The question replaces the fee deactivation question (`deactivateFee`),
 * which no new submission answers.
 */
export function asksManagementFeesDeducted(type: TransferRequestType): boolean {
    return type === 'transfer_out';
}

/** True when a submission of the type needs a document (SEP-359): a Demat and a Renunciation. */
export function needsSubmissionDocument(type: TransferRequestType): boolean {
    return type === 'demat' || type === 'renunciation';
}

/** The message of a missing mandatory document (SEP-359). */
export const TRANSFER_DOCUMENT_REQUIRED_MESSAGE = 'Please upload scanned copies of the documents.';

// ---------------------------------------------------------------------------
// The whole snapshot
// ---------------------------------------------------------------------------

/** The fields of the whole snapshot of the submitted form. */
const answersShape = {
    /** Q1a */
    clientName: requiredText,
    /** Q1b */
    clientReference: requiredText,
    /** Q1c */
    investmentManagerName: requiredText,
    /** Q2a: the request type (SEP-359). */
    requestType: transferRequestTypeSchema,
    /** Q2b of a Transfer IN, a Transfer OUT and an Internal Transfer. Null for the other types. */
    portfolios: transferPortfolioSchema.nullable(),
    /** Q2c of a Transfer IN, a Transfer OUT and an Internal Transfer. Null for the other types. */
    scope: transferScopeSchema.nullable(),
    /** Q2c "Partial additional comments": required when the scope is `partial`. */
    partialComments: optionalText,
    /**
     * The retired Q2d: "Transfers team to deactivate fee on account?". No new
     * submission answers it (SEP-359). An older ticket keeps its answer.
     */
    deactivateFee: yesNo.nullable(),
    /**
     * Q2d (SEP-359): have all outstanding management fees been deducted
     * before the transfer? Only a Transfer OUT asks it. A ticket from before
     * the question has none.
     */
    managementFeesDeducted: yesNo.nullable().default(null),
    /**
     * True when the IM acknowledged the warning of a No to Q2d. Null when the
     * form did not show the warning.
     */
    managementFeesWarningAcknowledged: z.boolean().nullable().default(null),
    /** Q3a of a Transfer IN and a Transfer OUT. Null for the other types. */
    counterpartyName: z.string().trim().max(500).nullable(),
    /** Q3b */
    counterpartyPhone: optionalShortText,
    /** Q3c */
    counterpartyEmail: z.string().trim().email().max(200).nullable(),
    /** Q3d */
    counterpartyFurtherContacts: optionalText,
    /**
     * Q4a. Only a transfer in asks it (SEP-317 section 1.2), so a transfer out
     * that is submitted after SEP-317 keeps null. An older ticket of either
     * direction keeps its answer.
     */
    valuationSeen: yesNo.nullable(),
    /** Q5 and Q6: required for a Transfer OUT, and null for each other type. */
    transferOut: transferOutAnswersSchema.nullable(),
    /** The Internal Transfer block. A ticket from before SEP-359 has none. */
    internalTransfer: transferInternalAnswersSchema.nullable().default(null),
    /** The Demat block. */
    demat: transferDematAnswersSchema.nullable().default(null),
    /** The Remat block. */
    remat: transferRematAnswersSchema.nullable().default(null),
    /** The Renunciation block. */
    renunciation: transferRenunciationAnswersSchema.nullable().default(null),
    /** The Stock Dump block. */
    stockDump: transferStockDumpAnswersSchema.nullable().default(null),
    additionalComments: optionalText,
};

/** The fields that the rules of the stored snapshot read. */
interface AnswersForRules {
    readonly requestType: TransferRequestType;
    readonly portfolios: string | null;
    readonly scope: string | null;
    readonly partialComments: string | null;
    readonly counterpartyName: string | null;
    readonly transferOut: unknown;
    readonly internalTransfer: unknown;
    readonly demat: unknown;
    readonly remat: unknown;
    readonly renunciation: unknown;
    readonly stockDump: unknown;
}

/**
 * The rules of the stored snapshot. A submission obeys them too:
 *   - The snapshot holds the block of its type, and no other block.
 *   - A type that asks Q2b and Q2c has both answers.
 *   - A Transfer IN and a Transfer OUT name the counterparty.
 *   - A partial transfer has comments.
 */
function refineAnswers(value: AnswersForRules, ctx: z.RefinementCtx): void {
    const own = transferRequestTypeBlock(value.requestType);
    for (const key of BLOCK_KEYS) {
        const present = value[key] !== null;
        if (key === own && !present) issue(ctx, [key], 'This request type needs these answers.');
        if (key !== own && present) issue(ctx, [key], 'This request type has no such answers.');
    }
    if (asksTransferPortfolios(value.requestType) && value.portfolios === null) {
        issue(ctx, ['portfolios'], ANSWER_THIS);
    }
    if (asksTransferScope(value.requestType) && value.scope === null) {
        issue(ctx, ['scope'], ANSWER_THIS);
    }
    if (asksCounterparty(value.requestType) && !value.counterpartyName?.trim()) {
        issue(ctx, ['counterpartyName'], ANSWER_THIS);
    }
    if (value.scope === 'partial' && !value.partialComments?.trim()) {
        issue(ctx, ['partialComments'], 'Add context for a partial transfer.');
    }
}

/** The whole snapshot of the submitted form. */
export const transferRequestAnswersSchema = z.object(answersShape).superRefine(refineAnswers);
export type TransferRequestAnswers = z.infer<typeof transferRequestAnswersSchema>;

/** The message of a No to Q2d with no acknowledgement of the warning (SEP-359). */
export const TRANSFER_MANAGEMENT_FEES_ACKNOWLEDGE_MESSAGE = 'Acknowledge the warning to continue.';

/** The message of the rule of SEP-317 section 1.1. */
export const TRANSFER_CONTACT_METHOD_MESSAGE =
    'Please provide at least one counterparty contact method.';

/**
 * The answers of a submission (SEP-317 sections 1.1, 1.2 and 2.8, SEP-359).
 * These rules apply to a new submission only, so a stored ticket from before
 * them stays valid:
 *   - A question that the type does not ask has no answer.
 *   - A Transfer IN and a Transfer OUT give Q3b or Q3c.
 *   - A Transfer IN answers Q4a.
 *   - A Transfer OUT answers Q2d, and a full Transfer OUT answers Q6c.
 *   - A No to Q2d needs the acknowledgement of the warning (SEP-359).
 *   - An Internal Transfer answers the Pershing questions (SEP-359).
 *   - Q5a is optional. `toStoredTransferAnswers` derives it from Q2c.
 */
export const transferRequestSubmissionAnswersSchema = z
    .object({
        ...answersShape,
        valuationSeen: yesNo.nullable().optional(),
        transferOut: transferOutSubmissionSchema.nullable(),
        internalTransfer: transferInternalSubmissionSchema.nullable().default(null),
    })
    .superRefine((value, ctx) => {
        refineAnswers(value, ctx);
        const type = value.requestType;
        if (!asksTransferPortfolios(type) && value.portfolios !== null) {
            issue(ctx, ['portfolios'], 'Not asked for this type.');
        }
        if (!asksTransferScope(type) && value.scope !== null) {
            issue(ctx, ['scope'], 'Not asked for this type.');
        }
        if (value.deactivateFee !== null) {
            issue(ctx, ['deactivateFee'], 'Not asked any more.');
        }
        if (asksManagementFeesDeducted(type)) {
            if (value.managementFeesDeducted === null) {
                issue(ctx, ['managementFeesDeducted'], ANSWER_THIS);
            }
            if (
                value.managementFeesDeducted === 'no' &&
                value.managementFeesWarningAcknowledged !== true
            ) {
                issue(
                    ctx,
                    ['managementFeesWarningAcknowledged'],
                    TRANSFER_MANAGEMENT_FEES_ACKNOWLEDGE_MESSAGE,
                );
            }
            if (
                value.managementFeesDeducted === 'yes' &&
                value.managementFeesWarningAcknowledged !== null
            ) {
                issue(ctx, ['managementFeesWarningAcknowledged'], 'Not asked for a Yes.');
            }
        } else if (
            value.managementFeesDeducted !== null ||
            value.managementFeesWarningAcknowledged !== null
        ) {
            issue(ctx, ['managementFeesDeducted'], 'Not asked for this type.');
        }
        if (asksCounterparty(type)) {
            if (!value.counterpartyPhone?.trim() && !value.counterpartyEmail?.trim()) {
                issue(ctx, ['counterpartyEmail'], TRANSFER_CONTACT_METHOD_MESSAGE);
            }
        } else if (
            value.counterpartyName !== null ||
            value.counterpartyPhone !== null ||
            value.counterpartyEmail !== null ||
            value.counterpartyFurtherContacts !== null
        ) {
            issue(ctx, ['counterpartyName'], 'Not asked for this type.');
        }
        if (type === 'transfer_in' && !value.valuationSeen) {
            issue(ctx, ['valuationSeen'], ANSWER_THIS);
        }
        if (type === 'transfer_out' && value.transferOut !== null) {
            const full = value.scope !== null && value.scope !== 'partial';
            if (full && value.transferOut.reason === null) {
                issue(ctx, ['transferOut', 'reason'], ANSWER_THIS);
            }
            if (!full && value.transferOut.reason !== null) {
                issue(ctx, ['transferOut', 'reason'], 'Not asked for a partial transfer.');
            }
        }
    });
export type TransferRequestSubmissionAnswers = z.infer<
    typeof transferRequestSubmissionAnswersSchema
>;

/**
 * The stored snapshot of a submission. Q5a comes from Q2c: a partial transfer
 * is partial, and each full transfer is full. Only a Transfer IN stores Q4a,
 * because only its form asks it.
 */
export function toStoredTransferAnswers(
    answers: TransferRequestSubmissionAnswers,
): TransferRequestAnswers {
    const transferOut =
        answers.transferOut === null
            ? null
            : {
                  ...answers.transferOut,
                  outScope: answers.scope === 'partial' ? ('partial' as const) : ('full' as const),
              };
    return {
        ...answers,
        valuationSeen:
            answers.requestType === 'transfer_in' ? (answers.valuationSeen ?? null) : null,
        transferOut,
    };
}

/**
 * The portfolio references that a snapshot names, for the search of the
 * Operations list. An Internal Transfer names two.
 */
export function transferAnswerPortfolios(
    answers: TransferRequestAnswers,
): TransferPortfolioChoice[] {
    if (answers.internalTransfer) {
        return [answers.internalTransfer.fromPortfolio, answers.internalTransfer.toPortfolio];
    }
    const block = answers.demat ?? answers.remat ?? answers.renunciation ?? answers.stockDump;
    return block ? [block.portfolio] : [];
}

// ---------------------------------------------------------------------------
// Desk: create and prefill
// ---------------------------------------------------------------------------

/** The choice of the IM or the EA for one holding of a transfer out (SEP-317 section 3). */
export const transferHoldingSelectionSchema = z.object({
    clientHoldingId: z.string().min(1),
    included: z.boolean(),
    /** The reason to remove a holding from a full in specie transfer. */
    removalReason: z.string().trim().max(2000).nullable().optional(),
    /** The optional comment on a selected holding of a partial transfer. */
    deskComment: z.string().trim().max(2000).nullable().optional(),
});
export type TransferHoldingSelection = z.infer<typeof transferHoldingSelectionSchema>;

/** The purposes of a document that the Desk attaches. The holdings file is an Ops upload. */
export const deskTransferDocumentPurposeSchema = z.enum(['general', 'portfolio_valuation']);
export type DeskTransferDocumentPurpose = z.infer<typeof deskTransferDocumentPurposeSchema>;

/** One staged upload that the submission attaches to the new ticket (SEP-317 section 1.3). */
export const transferSubmissionDocumentSchema = z.object({
    sourceUploadId: z.string().min(1),
    purpose: deskTransferDocumentPurposeSchema,
    documentType: z.string().trim().max(120).nullable(),
    description: z.string().trim().max(500).nullable(),
});
export type TransferSubmissionDocument = z.infer<typeof transferSubmissionDocumentSchema>;

export const createTransferRequestBodySchema = z.object({
    clientId: z.string().min(1),
    answers: transferRequestSubmissionAnswersSchema,
    /** A transfer out only. A transfer in sends none. */
    holdingSelections: z.array(transferHoldingSelectionSchema).max(1000).default([]),
    documents: z.array(transferSubmissionDocumentSchema).max(20).default([]),
});
export type CreateTransferRequestBody = z.infer<typeof createTransferRequestBodySchema>;

export const createTransferRequestResponseSchema = z.object({
    id: z.string(),
    reference: z.string(),
});
export type CreateTransferRequestResponse = z.infer<typeof createTransferRequestResponseSchema>;

/** One holding of the chosen client, for the selection on the checklist (SEP-317 section 3). */
export const transferPrefillHoldingSchema = z.object({
    clientHoldingId: z.string(),
    holdingName: z.string(),
    isin: z.string().nullable(),
    sedol: z.string().nullable(),
    portfolioRef: z.string(),
    holdingClass: z.string(),
    quantity: z.number(),
    valueGbp: z.number(),
    currency: z.string(),
});
export type TransferPrefillHolding = z.infer<typeof transferPrefillHoldingSchema>;

/** One portfolio that a dropdown of the checklist offers (SEP-359). */
export const transferPortfolioOptionSchema = z.object({
    portfolioId: z.string(),
    /** The portfolio reference, as the client record holds it. */
    reference: z.string(),
    accountType: z.string(),
    status: z.enum(['open', 'closed', 'suspended']),
    clientId: z.string(),
    clientName: z.string(),
});
export type TransferPortfolioOption = z.infer<typeof transferPortfolioOptionSchema>;

export const transferRequestPrefillSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    clientReference: z.string().nullable(),
    investmentManagerName: z.string().nullable(),
    /** The holdings of the client now. The form shows them for a transfer out, an internal transfer and a Remat. */
    holdings: z.array(transferPrefillHoldingSchema),
    /** The portfolios of the client that have a reference, in reference order (SEP-359). */
    portfolios: z.array(transferPortfolioOptionSchema),
});
export type TransferRequestPrefill = z.infer<typeof transferRequestPrefillSchema>;

/** The portfolios of each client of the book of the caller (SEP-359): the "to" dropdown of an internal transfer. */
export const transferBookPortfoliosResponseSchema = z.object({
    portfolios: z.array(transferPortfolioOptionSchema),
});
export type TransferBookPortfoliosResponse = z.infer<typeof transferBookPortfoliosResponseSchema>;

/** One security that the stock search of the checklist finds (SEP-359). */
export const transferSecurityResultSchema = z.object({
    name: z.string(),
    isin: z.string().nullable(),
    sedol: z.string().nullable(),
    ticker: z.string().nullable(),
});
export type TransferSecurityResult = z.infer<typeof transferSecurityResultSchema>;

export const transferSecuritySearchResponseSchema = z.object({
    results: z.array(transferSecurityResultSchema),
});
export type TransferSecuritySearchResponse = z.infer<typeof transferSecuritySearchResponseSchema>;

/**
 * The answer of `POST /api/transfer-drafts/documents`: one staged upload that
 * no ticket names yet. The route is raw Nest (multipart), so it is not in the
 * ts-rest router.
 */
export const transferDraftDocumentSchema = z.object({
    sourceUploadId: z.string(),
    filename: z.string(),
    sizeBytes: z.number().int(),
    contentType: z.string().nullable(),
});
export type TransferDraftDocument = z.infer<typeof transferDraftDocumentSchema>;

// ---------------------------------------------------------------------------
// Shared DTO parts
// ---------------------------------------------------------------------------

export const transferOwnerSchema = z.object({
    team: transferOwnerTeamSchema,
    userId: z.string().nullable(),
    display: z.string(),
});
export type TransferOwner = z.infer<typeof transferOwnerSchema>;

export const transferEventSchema = z.object({
    id: z.string(),
    eventType: transferEventTypeSchema,
    at: z.string(),
    actorName: z.string().nullable(),
    actorInitials: z.string().nullable(),
    summary: z.string(),
    field: z.string().nullable(),
    fromValue: z.string().nullable(),
    toValue: z.string().nullable(),
    note: z.string().nullable(),
    visibility: transferEventVisibilitySchema.nullable(),
});
export type TransferEvent = z.infer<typeof transferEventSchema>;

export const transferNoteSchema = z.object({
    id: z.string(),
    body: z.string(),
    visibility: transferNoteVisibilitySchema,
    authorName: z.string(),
    authorInitials: z.string(),
    createdAt: z.string(),
    /** The time of the last edit of the text (SEP-359). Null when nobody edited the note. */
    editedAt: z.string().nullable(),
    /** The name of the person who edited the text last (SEP-359). */
    editedByName: z.string().nullable(),
    /**
     * True when the reader can edit and remove the note (SEP-359): Operations
     * each note, an IM or an EA only an own note. The server checks it again.
     */
    canEdit: z.boolean(),
});
export type TransferNote = z.infer<typeof transferNoteSchema>;

export const transferDocumentSchema = z.object({
    id: z.string(),
    filename: z.string(),
    documentType: z.string().nullable(),
    /** An older document with no purpose reads as `general`. */
    purpose: transferDocumentPurposeSchema,
    description: z.string().nullable(),
    sizeBytes: z.number().int(),
    version: z.number().int(),
    uploadedAt: z.string(),
    uploadedByName: z.string(),
    uploadedByInitials: z.string(),
    /** The team of the uploader. Null when the history has no upload row for the document. */
    uploadedByTeam: transferOwnerTeamSchema.nullable(),
    contentUrl: z.string(),
});
export type TransferDocument = z.infer<typeof transferDocumentSchema>;

export const transferHoldingWorkflowLabelSchema = z.enum([
    'im_action_required',
    'awaiting_operations_review',
    'complete',
    'operations',
]);
export const transferHoldingNextActionSchema = z.enum(['im', 'operations']);
export const transferCompletionBlockSchema = z.enum([
    'awaiting_im',
    'awaiting_review',
    'not_settled',
]);

/** The latest response of the IM to a referral of a holding line (SEP-359). */
export const transferDeskResponseSchema = z.object({
    response: z.string(),
    /** True when the IM confirmed that the action is complete outside the platform. */
    actionCompleted: z.boolean(),
    respondedByName: z.string().nullable(),
    respondedAt: z.string().nullable(),
});
export type TransferDeskResponse = z.infer<typeof transferDeskResponseSchema>;

export const transferHoldingSchema = z.object({
    id: z.string(),
    holdingName: z.string(),
    isin: z.string().nullable(),
    sedol: z.string().nullable(),
    portfolioRef: z.string().nullable(),
    quantity: z.number().nullable(),
    valueGbp: z.number().nullable(),
    currency: z.string().nullable(),
    included: z.boolean().nullable(),
    selectionStatus: transferHoldingSelectionStatusSchema,
    /** The stored status of the line (SEP-317 section 4.2). */
    holdingStatus: transferHoldingStatusSchema,
    /** The note or the reason of the latest status that needs one. */
    statusReason: z.string().nullable(),
    /** Who acts next on the line (SEP-359). */
    workflowState: transferHoldingWorkflowStateSchema,
    /** The workflow label of the card: IM Action Required, Awaiting Operations Review, Complete or Operations. */
    workflowLabel: transferHoldingWorkflowLabelSchema,
    /** The team for the next action. Null for a complete line. */
    nextAction: transferHoldingNextActionSchema.nullable(),
    /** The statuses that Operations can choose for the line. Empty for a Stock Dump line (SEP-359). */
    statusOptions: z.array(transferHoldingStatusSchema),
    /** Why the line stops the completion of the ticket, or null (SEP-359). */
    completionBlock: transferCompletionBlockSchema.nullable(),
    /** The latest response of the IM, or null (SEP-359). */
    deskResponse: transferDeskResponseSchema.nullable(),
    /** False for an excluded line and a line that is No Longer Transferring (SEP-317 section 4.6). */
    active: z.boolean(),
    /** True when the line came from Operations (a transfer in), not from the client holdings. */
    addedByOperations: z.boolean(),
    exclusionReason: z.string().nullable(),
    /** The comment of the IM or the EA on a selected line (SEP-317 section 3.3). */
    deskComment: z.string().nullable(),
    confirmedQuantity: z.number().nullable(),
    confirmedValueGbp: z.number().nullable(),
    note: z.string().nullable(),
    /** The Holding Last Updated: the latest change of the line. Null when nothing changed it. */
    lastUpdatedAt: z.string().nullable(),
    lastUpdatedByName: z.string().nullable(),
    /** The holding notes, the newest first (SEP-317 section 5). */
    notes: z.array(transferNoteSchema),
    /** The holding history: the history rows that name the line, the newest first. */
    events: z.array(transferEventSchema),
});
export type TransferHolding = z.infer<typeof transferHoldingSchema>;

/**
 * One holding line as the Desk reads it (SEP-317 sections 4.4 and 7). It holds
 * the status that the Desk was told and the `desk` holding notes only. It holds
 * no reason or note of Operations that is internal, and no confirmed value.
 */
export const deskTransferHoldingSchema = z.object({
    id: z.string(),
    holdingName: z.string(),
    isin: z.string().nullable(),
    sedol: z.string().nullable(),
    portfolioRef: z.string().nullable(),
    quantity: z.number().nullable(),
    valueGbp: z.number().nullable(),
    currency: z.string().nullable(),
    included: z.boolean().nullable(),
    /** The latest status that Operations made visible to the Desk. */
    holdingStatus: transferHoldingStatusSchema,
    /** Who acts next on the line (SEP-359). */
    workflowState: transferHoldingWorkflowStateSchema,
    workflowLabel: transferHoldingWorkflowLabelSchema,
    /** The team for the next action. Null for a complete line. */
    nextAction: transferHoldingNextActionSchema.nullable(),
    /** The comment of Operations with the referral of the line. Null when the line is not referred. */
    referralComment: z.string().nullable(),
    /** The latest response of the IM, or null (SEP-359). */
    deskResponse: transferDeskResponseSchema.nullable(),
    deskComment: z.string().nullable(),
    /** The time of the latest change that the Desk can see. Null when there is none. */
    lastUpdatedAt: z.string().nullable(),
    /** The holding notes that are visible to the Desk, the newest first. */
    notes: z.array(transferNoteSchema),
});
export type DeskTransferHolding = z.infer<typeof deskTransferHoldingSchema>;

export const transferEmailSchema = z.object({
    id: z.string(),
    templateKey: z.string(),
    templateLabel: z.string(),
    /** The mailbox that sent the email. Null on an email from before SEP-317. */
    fromAddress: z.string().nullable(),
    toRecipients: z.array(z.string()),
    ccRecipients: z.array(z.string()),
    subject: z.string(),
    body: z.string(),
    attachmentNames: z.array(z.string()),
    sentByName: z.string(),
    sentAt: z.string(),
    deliveryStatus: transferEmailDeliverySchema,
    deliveryError: z.string().nullable(),
    /** The field values of the send, by the field label. */
    fieldValues: z.array(z.object({ label: z.string(), value: z.string() })),
    /** The name of the holding line of a per-holding template. */
    holdingName: z.string().nullable(),
    /** The expected attachments that Operations chose to send without. */
    confirmedMissingAttachments: z.array(z.string()),
});
export type TransferEmail = z.infer<typeof transferEmailSchema>;

export const transferStageHistoryEntrySchema = z.object({
    stage: transferStageSchema,
    reachedAt: z.string().nullable(),
});
export type TransferStageHistoryEntry = z.infer<typeof transferStageHistoryEntrySchema>;

/**
 * The open challenge of a completion (SEP-317 section 10). `completedAt` is
 * the completion that the IM or the EA challenged. A recall clears the
 * completion of the ticket, and the challenge keeps this time.
 */
export const transferChallengeSchema = z.object({
    raisedAt: z.string(),
    raisedByName: z.string().nullable(),
    reason: z.string(),
    completedAt: z.string().nullable(),
});
export type TransferChallenge = z.infer<typeof transferChallengeSchema>;

/** One completion of a ticket. The history keeps each one (SEP-317 section 10.3). */
export const transferCompletionSchema = z.object({
    at: z.string(),
    byName: z.string().nullable(),
});
export type TransferCompletion = z.infer<typeof transferCompletionSchema>;

/**
 * The tabs of the Operations list (SEP-317 section 10). `for_review` holds each
 * ticket with an open challenge, at any stage. `active` and `completed` hold
 * the tickets with no open challenge.
 */
export const TRANSFER_LIST_TABS = ['active', 'completed', 'for_review'] as const;
export const transferListTabSchema = z.enum(TRANSFER_LIST_TABS);
export type TransferListTab = z.infer<typeof transferListTabSchema>;

/**
 * The views of the Operations list: the three tabs, and `all` (Zack, SEP-359).
 * `all` holds every ticket of every stage, with or without a challenge. It is
 * not the tab of a ticket: each ticket has one tab of `TRANSFER_LIST_TABS`.
 */
export const TRANSFER_LIST_VIEWS = [...TRANSFER_LIST_TABS, 'all'] as const;
export const transferListViewSchema = z.enum(TRANSFER_LIST_VIEWS);
export type TransferListView = z.infer<typeof transferListViewSchema>;

/** The count of tickets on each tab, and of every ticket. */
export const transferTabCountsSchema = z.object({
    active: z.number().int(),
    completed: z.number().int(),
    forReview: z.number().int(),
    all: z.number().int(),
});
export type TransferTabCounts = z.infer<typeof transferTabCountsSchema>;

/**
 * The sale of the securities of a Full - In Cash Transfer OUT (SEP-359). The
 * IM confirms that the sell orders are placed, and only then can Operations
 * send the ticket to Titan. Null for a ticket that needs no sale.
 */
export const transferSecuritiesSaleSchema = z.object({
    confirmedAt: z.string().nullable(),
    confirmedByName: z.string().nullable(),
});
export type TransferSecuritiesSale = z.infer<typeof transferSecuritiesSaleSchema>;

// ---------------------------------------------------------------------------
// Ops: list and detail
// ---------------------------------------------------------------------------

export const opsTransferRowSchema = z.object({
    id: z.string(),
    reference: z.string(),
    happyFoxRef: z.string().nullable(),
    clientName: z.string(),
    clientReference: z.string().nullable(),
    transferType: transferTypeSchema.nullable(),
    /** Q2a: the request type of the checklist (SEP-359). */
    requestType: transferRequestTypeSchema,
    /** Null for a request type with no counterparty. */
    counterpartyName: z.string().nullable(),
    stage: transferStageSchema,
    owner: transferOwnerSchema,
    raisedAt: z.string(),
    lastUpdatedAt: z.string(),
    /** True when the ticket has an update that the signed-in Operations user has not read (SEP-317). */
    unread: z.boolean(),
    /** The open challenge of the completion, or null (SEP-317 section 10). */
    challenge: transferChallengeSchema.nullable(),
});
export type OpsTransferRow = z.infer<typeof opsTransferRowSchema>;

export const opsTransferListQuerySchema = z.object({
    tab: transferListViewSchema.default('active'),
    q: z.string().max(200).optional(),
});
export type OpsTransferListQuery = z.infer<typeof opsTransferListQuerySchema>;

export const opsTransferListResponseSchema = z.object({
    rows: z.array(opsTransferRowSchema),
    /** One count per stage over ALL tickets. The search does not change it. */
    stageCounts: z.record(transferStageSchema, z.number().int()),
    /** One count per tab over ALL tickets. The search does not change it. */
    tabCounts: transferTabCountsSchema,
    total: z.number().int(),
    /**
     * The tickets of the OTHER tabs that match the search. The search reads
     * all tickets, so a match on another tab is not lost. Zero when there
     * is no search.
     */
    otherTabMatches: z.number().int(),
    /** The tickets of each tab that match the search. Each count is zero when there is no search. */
    matchesByTab: transferTabCountsSchema,
});
export type OpsTransferListResponse = z.infer<typeof opsTransferListResponseSchema>;

export const opsTransferDetailSchema = opsTransferRowSchema.extend({
    clientId: z.string(),
    portfolios: transferPortfolioSchema.nullable(),
    scope: transferScopeSchema.nullable(),
    answers: transferRequestAnswersSchema,
    submittedByName: z.string(),
    investmentManagerName: z.string().nullable(),
    /** Q5d. Operations fills it. */
    tradingBlockApplied: transferYesNoSchema.nullable(),
    /** Q6a. Operations fills it. */
    feesToDebit: z.string().nullable(),
    completedAt: z.string().nullable(),
    completedByName: z.string().nullable(),
    /** Each completion of the ticket, the first one first (SEP-317 section 10.3). */
    completions: z.array(transferCompletionSchema),
    /** The name of the person who set the Happy Fox Ref last (SEP-317). */
    happyFoxRefSetByName: z.string().nullable(),
    happyFoxRefSetAt: z.string().nullable(),
    /**
     * The read point of the signed-in user when the ticket is unread, else
     * null. Each event after this time is new for the user (SEP-317 section
     * 6.2). A read of the ticket does not change it: the page marks the ticket
     * read with a separate call.
     */
    unreadSince: z.string().nullable(),
    stageHistory: z.array(transferStageHistoryEntrySchema),
    holdings: z.array(transferHoldingSchema),
    /** The remaining active holdings (SEP-317 section 4.6). */
    activeHoldingCount: z.number().int(),
    documents: z.array(transferDocumentSchema),
    /** The notes of the ticket. A holding note is on its line. */
    notes: z.array(transferNoteSchema),
    emails: z.array(transferEmailSchema),
    events: z.array(transferEventSchema),
    /** The sale of the securities of a full transfer in cash, or null (SEP-359). */
    securitiesSale: transferSecuritiesSaleSchema.nullable(),
});
export type OpsTransferDetail = z.infer<typeof opsTransferDetailSchema>;

export const opsTransferOwnerOptionsSchema = z.object({
    users: z.array(
        z.object({
            userId: z.string(),
            name: z.string(),
            team: transferOwnerTeamSchema,
        }),
    ),
});
export type OpsTransferOwnerOptions = z.infer<typeof opsTransferOwnerOptionsSchema>;

// ---------------------------------------------------------------------------
// Desk: list and detail
// ---------------------------------------------------------------------------

export const deskTransferRowSchema = z.object({
    id: z.string(),
    reference: z.string(),
    clientName: z.string(),
    /** Q2a: the request type of the checklist (SEP-359). */
    requestType: transferRequestTypeSchema,
    /** Null for a request type with no counterparty. */
    counterpartyName: z.string().nullable(),
    stage: transferStageSchema,
    owner: transferOwnerSchema,
    raisedAt: z.string(),
    lastUpdatedAt: z.string(),
    /** The open challenge of the completion, or null (SEP-317 section 10). */
    challenge: transferChallengeSchema.nullable(),
});
export type DeskTransferRow = z.infer<typeof deskTransferRowSchema>;

export const deskTransferListResponseSchema = z.object({
    rows: z.array(deskTransferRowSchema),
});
export type DeskTransferListResponse = z.infer<typeof deskTransferListResponseSchema>;

export const deskTransferDetailSchema = deskTransferRowSchema.extend({
    answers: transferRequestAnswersSchema,
    portfolios: transferPortfolioSchema.nullable(),
    scope: transferScopeSchema.nullable(),
    documents: z.array(transferDocumentSchema),
    /** Desk-visible notes of the ticket only. A holding note is on its line. */
    notes: z.array(transferNoteSchema),
    /** The holding lines, with the Desk-visible holding notes only (SEP-317). */
    holdings: z.array(deskTransferHoldingSchema),
    /** Desk-visible events only. */
    events: z.array(transferEventSchema),
    stageHistory: z.array(transferStageHistoryEntrySchema),
    /** Each completion of the ticket, the first one first (SEP-317 section 10.3). */
    completions: z.array(transferCompletionSchema),
    /** The sale of the securities of a full transfer in cash, or null (SEP-359). */
    securitiesSale: transferSecuritiesSaleSchema.nullable(),
});
export type DeskTransferDetail = z.infer<typeof deskTransferDetailSchema>;

// ---------------------------------------------------------------------------
// Mutation bodies
// ---------------------------------------------------------------------------

export const setTransferTypeBodySchema = z.object({
    transferType: transferTypeSchema.nullable(),
});
export type SetTransferTypeBody = z.infer<typeof setTransferTypeBodySchema>;

export const setHappyFoxRefBodySchema = z.object({
    happyFoxRef: z.string().trim().max(60).nullable(),
});
export type SetHappyFoxRefBody = z.infer<typeof setHappyFoxRefBodySchema>;

export const setOperationsAnswersBodySchema = z.object({
    tradingBlockApplied: transferYesNoSchema.nullable().optional(),
    feesToDebit: z.string().trim().max(200).nullable().optional(),
});
export type SetOperationsAnswersBody = z.infer<typeof setOperationsAnswersBodySchema>;

export const changeTransferStageBodySchema = z.object({
    stage: activeTransferStageSchema,
    /** The Happy Fox Ref to set with the move (SEP-317 section 9). Blank keeps the ref. */
    happyFoxRef: z.string().trim().max(60).optional(),
});
export type ChangeTransferStageBody = z.infer<typeof changeTransferStageBodySchema>;

export const changeTransferOwnerBodySchema = z.object({
    team: transferOwnerTeamSchema,
    userId: z.string().nullable(),
    note: z.string().trim().max(2000).optional(),
});
export type ChangeTransferOwnerBody = z.infer<typeof changeTransferOwnerBodySchema>;

export const addTransferNoteBodySchema = z.object({
    body: z.string().trim().min(1).max(4000),
    visibility: transferNoteVisibilitySchema,
});
export type AddTransferNoteBody = z.infer<typeof addTransferNoteBodySchema>;

/** A note from the Desk is always visible to the Desk, so the body has no visibility. */
export const deskAddTransferNoteBodySchema = z.object({
    body: z.string().trim().min(1).max(4000),
});
export type DeskAddTransferNoteBody = z.infer<typeof deskAddTransferNoteBodySchema>;

/** A new text for a note (SEP-359). The visibility does not change. */
export const editTransferNoteBodySchema = z.object({
    body: z.string().trim().min(1).max(4000),
});
export type EditTransferNoteBody = z.infer<typeof editTransferNoteBodySchema>;

/**
 * Operations edits answers of the submitted checklist (SEP-359). Each
 * `question` is an editable answer (`TRANSFER_EDITABLE_ANSWERS`), and a blank
 * `value` is no answer. The note is mandatory: the server refuses a blank one
 * with NOTE_REQUIRED.
 */
export const editTransferAnswersBodySchema = z.object({
    changes: z
        .array(
            z.object({
                question: z.string().min(1).max(80),
                value: z.string().max(4000).nullable(),
            }),
        )
        .min(1)
        .max(60),
    note: z.string().trim().max(2000),
});
export type EditTransferAnswersBody = z.infer<typeof editTransferAnswersBodySchema>;

export const returnTransferToOperationsBodySchema = z.object({
    note: z.string().trim().max(2000).optional(),
});
export type ReturnTransferToOperationsBody = z.infer<typeof returnTransferToOperationsBodySchema>;

export const transferHoldingInclusionBodySchema = z.object({
    included: z.boolean(),
    reason: z.string().trim().max(2000).optional(),
});
export type TransferHoldingInclusionBody = z.infer<typeof transferHoldingInclusionBodySchema>;

export const transferHoldingConfirmationBodySchema = z.object({
    confirmedQuantity: z.number(),
    confirmedValueGbp: z.number(),
    note: z.string().trim().max(2000).optional(),
});
export type TransferHoldingConfirmationBody = z.infer<typeof transferHoldingConfirmationBodySchema>;

/** Operations sets the status of one holding line (SEP-317 section 4.2). */
export const changeTransferHoldingStatusBodySchema = z.object({
    holdingStatus: transferHoldingStatusSchema,
    /** The note or the reason. The rule of the status says when it is required. */
    note: z.string().trim().max(4000).optional(),
    /** Who can see the note. The comment of a referral status is always visible to the Desk. */
    visibility: transferNoteVisibilitySchema.default('internal'),
});
export type ChangeTransferHoldingStatusBody = z.infer<typeof changeTransferHoldingStatusBodySchema>;

/** Operations adds a note to one holding line (SEP-317 section 5). */
export const addTransferHoldingNoteBodySchema = z.object({
    body: z.string().trim().min(1).max(4000),
    visibility: transferNoteVisibilitySchema,
});
export type AddTransferHoldingNoteBody = z.infer<typeof addTransferHoldingNoteBodySchema>;

/**
 * Operations confirms many holding lines in one act (SEP-317 section 4.1). A
 * confirmed value that is not given takes the value of the line.
 */
export const confirmTransferHoldingsBodySchema = z.object({
    holdings: z
        .array(
            z.object({
                holdingId: z.string().min(1),
                confirmedQuantity: z.number().nonnegative().nullable().optional(),
                confirmedValueGbp: z.number().nonnegative().nullable().optional(),
            }),
        )
        .min(1)
        .max(1000),
});
export type ConfirmTransferHoldingsBody = z.infer<typeof confirmTransferHoldingsBodySchema>;

/** Operations adds one holding line to a transfer in, from the holdings file (SEP-317 section 2). */
export const addTransferHoldingBodySchema = z.object({
    holdingName: z.string().trim().min(1).max(200),
    isin: z.string().trim().max(12).optional(),
    sedol: z.string().trim().max(7).optional(),
    portfolioRef: z.string().trim().max(60).optional(),
    quantity: z.number().nonnegative(),
    valueGbp: z.number().nonnegative(),
    currency: z
        .string()
        .trim()
        .regex(/^[A-Za-z]{3}$/)
        .default('GBP'),
});
export type AddTransferHoldingBody = z.infer<typeof addTransferHoldingBodySchema>;

export const recallTransferBodySchema = z.object({
    stage: activeTransferStageSchema,
    reason: z.string().trim().min(1).max(2000),
    /** The Happy Fox Ref to set with the recall (SEP-317 section 9). Blank keeps the ref. */
    happyFoxRef: z.string().trim().max(60).optional(),
});
export type RecallTransferBody = z.infer<typeof recallTransferBodySchema>;

/** Operations completes a ticket with the completion checks of its type (SEP-359). */
export const markTransferCompleteBodySchema = z.object({
    checks: z.array(transferCompletionCheckSchema).max(20).default([]),
});
export type MarkTransferCompleteBody = z.infer<typeof markTransferCompleteBodySchema>;

/** The IM or the EA responds to a referral of a holding line (SEP-359). */
export const respondToTransferHoldingBodySchema = z.object({
    response: z.string().trim().min(1).max(4000),
    actionCompleted: z.boolean().default(false),
});
export type RespondToTransferHoldingBody = z.infer<typeof respondToTransferHoldingBodySchema>;

/** The Desk challenges a completion. The explanation is mandatory (SEP-317 section 10.1). */
export const challengeTransferCompletionBodySchema = z.object({
    reason: z.string().trim().min(1).max(2000),
});
export type ChallengeTransferCompletionBody = z.infer<typeof challengeTransferCompletionBodySchema>;

// ---------------------------------------------------------------------------
// Ops: unread updates (SEP-317 section 6)
// ---------------------------------------------------------------------------

/** One ticket with an unread update, with its latest update. */
export const opsTransferUpdateSchema = z.object({
    transferId: z.string(),
    reference: z.string(),
    clientName: z.string(),
    stage: transferStageSchema,
    /** The tab of the Operations list that holds the ticket. */
    tab: transferListTabSchema,
    /** The type of the latest unread update. */
    eventType: transferEventTypeSchema,
    summary: z.string(),
    actorName: z.string().nullable(),
    at: z.string(),
    /** The count of unread updates on the ticket. */
    updateCount: z.number().int(),
});
export type OpsTransferUpdate = z.infer<typeof opsTransferUpdateSchema>;

/** The tickets with an unread update for the signed-in user, latest update first. */
export const opsTransferUpdatesResponseSchema = z.object({
    transferCount: z.number().int(),
    updates: z.array(opsTransferUpdateSchema),
});
export type OpsTransferUpdatesResponse = z.infer<typeof opsTransferUpdatesResponseSchema>;

/** The answer of a mark-read call: the new read point of the user. */
export const opsTransferReadResponseSchema = z.object({ readThrough: z.string() });
export type OpsTransferReadResponse = z.infer<typeof opsTransferReadResponseSchema>;

// ---------------------------------------------------------------------------
// Ops: email
// ---------------------------------------------------------------------------

export const transferEmailTemplateListSchema = z.array(
    z.object({
        key: z.string(),
        label: z.string(),
        /** True when the template names one holding line (SEP-317 sections 14.4 to 14.6). */
        perHolding: z.boolean(),
    }),
);
export type TransferEmailTemplateList = z.infer<typeof transferEmailTemplateListSchema>;

export const transferEmailPreviewBodySchema = z.object({
    templateKey: z.string().min(1),
    /** The holding line of a per-holding template. Null or absent: the first active line. */
    holdingId: z.string().nullable().optional(),
});
export type TransferEmailPreviewBody = z.infer<typeof transferEmailPreviewBodySchema>;

/**
 * One field of a template (SEP-317 section 11.2). The label is also the
 * placeholder in the subject and the body: `[label]`. The server fills
 * `value` from the client, the ticket and the holding line. Operations can
 * change each value.
 */
export const transferEmailFieldSchema = z.object({
    key: z.string(),
    label: z.string(),
    required: z.boolean(),
    /** True for a value with more than one line, for example a name and address. */
    multiline: z.boolean(),
    /** True for a value that comes from the holding line. */
    fromHolding: z.boolean(),
    value: z.string(),
});
export type TransferEmailField = z.infer<typeof transferEmailFieldSchema>;

export const transferEmailPreviewSchema = z.object({
    templateKey: z.string(),
    perHolding: z.boolean(),
    /** The holding line that gave the holding values. Null when there is none. */
    holdingId: z.string().nullable(),
    fields: z.array(transferEmailFieldSchema),
    /** The subject with the `[label]` placeholders. */
    subject: z.string(),
    /** The body with the `[label]` placeholders. */
    body: z.string(),
    /** The mailbox that sends the email (SEP-317 section 12). */
    fromAddress: z.string(),
    toRecipients: z.array(z.string()),
    /** The Transfers mailbox. The server always copies it when it is configured. */
    fixedCcRecipients: z.array(z.string()),
    /** The attachments that the template normally includes. A missing one gives a warning only. */
    expectedAttachments: z.array(z.string()),
});
export type TransferEmailPreview = z.infer<typeof transferEmailPreviewSchema>;

export const sendTransferEmailBodySchema = z.object({
    templateKey: z.string().min(1),
    holdingId: z.string().nullable().optional(),
    /** The value of each field, by the field key. The server checks each required field again. */
    fieldValues: z.record(z.string(), z.string().max(2000)),
    toRecipients: z.array(z.string().email()).min(1),
    /** The added copy recipients. The server adds the Transfers mailbox. */
    ccRecipients: z.array(z.string().email()),
    /** The subject. The server fills each `[label]` placeholder. */
    subject: z.string().trim().min(1).max(300),
    /** The body. The server fills each `[label]` placeholder. */
    body: z.string().trim().min(1).max(20000),
    attachmentDocumentIds: z.array(z.string()).max(10),
    /**
     * True when Operations read the warning of a missing expected attachment
     * and chose to send. Without it, a missing attachment gives a 409.
     */
    confirmMissingAttachments: z.boolean().optional(),
});
export type SendTransferEmailBody = z.infer<typeof sendTransferEmailBodySchema>;

/**
 * The refusal of a send. `missingFields` names the blank required fields
 * (REQUIRED_FIELD_MISSING). `missingAttachments` names the missing expected
 * attachments (EXPECTED_ATTACHMENT_MISSING).
 */
export const transferEmailRefusalSchema = z.object({
    error: z.string(),
    code: z.string().optional(),
    missingFields: z.array(z.string()).optional(),
    missingAttachments: z.array(z.string()).optional(),
});
export type TransferEmailRefusal = z.infer<typeof transferEmailRefusalSchema>;
